import React, { useState, useEffect } from 'react';
import { X, Save, Copy, ExternalLink, Shuffle } from 'lucide-react';


import { QRCodeEditService } from '../../../services/qrCodeEditService';
import { TrackingUrlService } from '../../../services/trackingUrlService';
import { supabase } from '../../../lib/supabase';
import ErrorAlert from '../../../components/ui/ErrorAlert';

export function QRCodeEditModal({ qrCode, isOpen, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    content: '',
    short_url: '',
    is_active: true,
    expiry_date: '',
    max_scans: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [shortCodeSuggestions, setShortCodeSuggestions] = useState([]);
  const [shortCodeError, setShortCodeError] = useState('');
  const [trackableUrl, setTrackableUrl] = useState('');
  const [isGeneratingShortCode, setIsGeneratingShortCode] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);

  // Function to normalize URLs by automatically adding protocol
  const normalizeUrl = (url) => {
    if (!url || typeof url !== 'string') return url;
    
    const trimmedUrl = url?.trim();
    if (!trimmedUrl) return trimmedUrl;
    
    // If URL already has a protocol, return as-is
    if (trimmedUrl?.match(/^https?:\/\//i)) {
      return trimmedUrl;
    }
    
    // If URL starts with www. or looks like a domain (contains dots), add https://
    if (trimmedUrl?.match(/^www\./i) || 
        trimmedUrl?.match(/^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.[a-zA-Z]{2,}(?:\/.*)?$/)) {
      return `https://${trimmedUrl}`;
    }
    
    // If it looks like a local development URL (localhost, 127.0.0.1, etc.), use http://
    if (trimmedUrl?.match(/^(localhost|127\.0\.0\.1|0\.0\.0\.0|192\.168\.|10\.|172\.)/i)) {
      return `http://${trimmedUrl}`;
    }
    
    // For other cases that might be domains but don't match above patterns, try https://
    if (trimmedUrl?.includes('.') && !trimmedUrl?.includes(' ') && !trimmedUrl?.includes('@')) {
      return `https://${trimmedUrl}`;
    }
    
    // Return as-is if it doesn't look like a URL
    return trimmedUrl;
  };

  // Generate random short code
  const generateRandomShortCode = async () => {
    setIsGeneratingShortCode(true);
    try {
      const { data, error } = await supabase?.rpc('generate_short_url');
      if (error) throw new Error(error?.message);
      if (data) {
        setFormData(prev => ({ ...prev, short_url: data }));
        setTrackableUrl(TrackingUrlService?.generateTrackingUrl(data));
        setShortCodeError('');
      }
    } catch (error) {
      console.log('Short code generation error:', error);
      setError('Failed to generate random code. Please try again.');
    } finally {
      setIsGeneratingShortCode(false);
    }
  };

  // Generate suggestions based on name
  const generateSuggestions = async () => {
    if (!formData?.name?.trim()) {
      setError('Please enter a QR code name first to generate suggestions');
      return;
    }

    setIsLoading(true);
    try {
      const result = await TrackingUrlService?.getShortCodeSuggestions(formData?.name);
      if (result?.success && result?.suggestions?.length > 0) {
        setShortCodeSuggestions(result?.suggestions);
        setShowSuggestions(true);
        setShortCodeError('');
      } else {
        setShortCodeError('Unable to generate suggestions. Try entering a short code manually.');
      }
    } catch (error) {
      console.log('Error generating suggestions:', error);
      setShortCodeError('Failed to generate suggestions');
    } finally {
      setIsLoading(false);
    }
  };

  // Apply suggestion
  const applySuggestion = (suggestion) => {
    setFormData(prev => ({ ...prev, short_url: suggestion }));
    setTrackableUrl(TrackingUrlService?.generateTrackingUrl(suggestion));
    setShowSuggestions(false);
    setShortCodeSuggestions([]);
    setShortCodeError('');
  };

  // Handle short code input change
  const handleShortCodeChange = (value) => {
    const cleanValue = value?.toLowerCase()?.replace(/[^a-z0-9_-]/g, '');
    setFormData(prev => ({ ...prev, short_url: cleanValue }));
    setTrackableUrl(TrackingUrlService?.generateTrackingUrl(cleanValue));
    
    // Clear previous error when user starts typing
    if (shortCodeError) {
      setShortCodeError('');
    }
    
    // Hide suggestions when user is typing their own
    if (showSuggestions) {
      setShowSuggestions(false);
    }
  };

  // Copy trackable URL
  const copyTrackableUrl = async () => {
    if (!trackableUrl) return;
    try {
      await navigator.clipboard?.writeText(trackableUrl);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 3000);
    } catch (error) {
      setError('Failed to copy URL');
    }
  };

  // Initialize form data when modal opens
  useEffect(() => {
    if (qrCode && isOpen) {
      setFormData({
        name: qrCode?.name || '',
        content: qrCode?.display_url || qrCode?.content || '', // Use display_url for user input
        short_url: qrCode?.short_url || '',
        is_active: qrCode?.is_active ?? true,
        expiry_date: qrCode?.expiry_date ? qrCode?.expiry_date?.split('T')?.[0] : '',
        max_scans: qrCode?.max_scans || '',
      });
      
      // Set initial trackable URL
      setTrackableUrl(TrackingUrlService?.generateTrackingUrl(qrCode?.short_url || ''));
      
      // Reset states
      setError('');
      setShortCodeError('');
      setShortCodeSuggestions([]);
      setShowSuggestions(false);
      setUrlCopied(false);
    }
  }, [qrCode, isOpen]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!qrCode?.id) return;

    // Validate required fields
    if (!formData?.name?.trim()) {
      setError('QR code name is required');
      return;
    }

    if (!formData?.content?.trim()) {
      setError('URL/Content is required');
      return;
    }

    if (!formData?.short_url?.trim()) {
      setError('Short code is required');
      return;
    }

    // Validate short code format
    if (!/^[a-z0-9_-]{2,50}$/?.test(formData?.short_url)) {
      setError('Short code must be 2-50 characters long and contain only lowercase letters, numbers, underscores, and hyphens');
      return;
    }

    // Validate URL format for URL type QR codes
    const normalizedDisplayUrl = normalizeUrl(formData?.content?.trim());
    if (qrCode?.qr_type === 'url') {
      try {
        new URL(normalizedDisplayUrl);
      } catch (error) {
        setError('Please enter a valid URL (e.g., https://example.com)');
        return;
      }
    }

    setIsLoading(true);
    setError('');

    try {
      // Check if short code is available (excluding current QR)
      const availability = await TrackingUrlService?.validateShortCodeAvailability(
        formData?.short_url?.trim(),
        qrCode?.id
      );

      if (!availability?.isAvailable) {
        if (availability?.suggestions?.length > 0) {
          setShortCodeSuggestions(availability?.suggestions);
          setShowSuggestions(true);
        }
        setError(availability?.error || 'This short code is already taken');
        setIsLoading(false);
        return;
      }

      // Generate the tracking URL for content field
      const trackingUrl = TrackingUrlService?.generateTrackingUrl(formData?.short_url?.trim());

      // Prepare update data with proper field mapping
      const updateData = {
        name: formData?.name?.trim(),
        display_url: normalizedDisplayUrl, // User-entered destination URL
        content: trackingUrl, // Tracking URL for QR code scanning
        is_active: formData?.is_active,
        expiry_date: formData?.expiry_date ? new Date(formData?.expiry_date)?.toISOString() : null,
        max_scans: formData?.max_scans ? parseInt(formData?.max_scans) : null,
        short_url: formData?.short_url?.trim(),
        tracking_url: trackingUrl, // Also update tracking_url field
      };

      const result = await QRCodeEditService?.updateQRCode(qrCode?.id, updateData);

      if (result?.success) {
        // ENHANCED: Call parent callback with updated QR code for immediate UI feedback
        // Real-time subscriptions will handle the actual state synchronization
        if (onSave) { // Changed from onUpdate to onSave to match parent component
          onSave(result?.qrCode);
        }
        onClose();
      } else {
        // Handle specific error cases
        if (result?.error?.includes('duplicate') || result?.error?.includes('unique')) {
          setError('This short code is already taken. Please choose a different one.');
        } else {
          setError(result?.error || 'Failed to update QR code');
        }
      }
    } catch (error) {
      console.log('Error updating QR code:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Edit QR Code</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <ErrorAlert 
              message={error} 
              onClose={() => setError('')}
              onDismiss={() => setError('')}
              className="mb-4"
            />
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* QR Code Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                QR Code Name *
              </label>
              <input
                type="text"
                value={formData?.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e?.target?.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter a descriptive name for your QR code"
                required
              />
            </div>

            {/* Destination URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Destination URL *
              </label>
              <input
                type="url"
                value={formData?.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e?.target?.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://example.com"
                required
              />
            </div>

            {/* Short Code Section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Short Code *
                </label>
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={generateSuggestions}
                    disabled={isLoading || !formData?.name?.trim()}
                    className="text-sm text-green-600 hover:text-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Get suggestions from name
                  </button>
                  <button
                    type="button"
                    onClick={generateRandomShortCode}
                    disabled={isGeneratingShortCode}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50"
                  >
                    {isGeneratingShortCode ? (
                      <span className="flex items-center">
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-1"></div>
                        Generating...
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <Shuffle size={14} className="mr-1" />
                        Generate Random
                      </span>
                    )}
                  </button>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500 whitespace-nowrap">
                  {(import.meta.env?.VITE_TRACKING_BASE_URL || 'https://qr.taylortravelmanagement.com')?.replace(/^https?:\/\//, '')}/
                </span>
                <input
                  type="text"
                  value={formData?.short_url}
                  onChange={(e) => handleShortCodeChange(e?.target?.value)}
                  className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    shortCodeError ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter or generate short code"
                />
              </div>
              
              {shortCodeError && (
                <p className="text-sm text-red-600 mt-1">{shortCodeError}</p>
              )}
              
              <p className="text-xs text-gray-500 mt-1">
                Allowed: lowercase letters, numbers, underscores, and hyphens (2-50 characters)
              </p>
            </div>

            {/* Suggestions Display */}
            {showSuggestions && shortCodeSuggestions?.length > 0 && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-gray-700">Available suggestions:</p>
                  <button
                    type="button"
                    onClick={() => setShowSuggestions(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <X size={16} />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {shortCodeSuggestions?.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => applySuggestion(suggestion)}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Trackable URL Display */}
            {trackableUrl && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Trackable URL Preview
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={trackableUrl}
                    readOnly
                    className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-600 text-sm"
                  />
                  <button
                    type="button"
                    onClick={copyTrackableUrl}
                    className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                    title="Copy to clipboard"
                  >
                    <Copy size={16} />
                  </button>
                  <a
                    href={trackableUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                    title="Open in new tab"
                  >
                    <ExternalLink size={16} />
                  </a>
                </div>
                {urlCopied && (
                  <p className="text-xs text-green-600 mt-1">✓ Copied to clipboard</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  This URL will track scans before redirecting to your destination
                </p>
              </div>
            )}

            {/* Advanced Settings */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-4">Advanced Settings (Optional)</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiry Date
                  </label>
                  <input
                    type="date"
                    value={formData?.expiry_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Scans
                  </label>
                  <input
                    type="number"
                    value={formData?.max_scans}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_scans: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Unlimited"
                    min="1"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData?.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e?.target?.checked }))}
                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">QR code is active</span>
                </label>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} className="mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}